import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Card,
  Collapse,
  Descriptions,
  Drawer,
  Select,
  Skeleton,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { RobotOutlined, SyncOutlined } from '@ant-design/icons';
import aiSummaryApi from '../../api/ai-summary-api';
import { formatDate } from '../../utils/formatDate';
import styles from './AiSummaryCard.module.scss';

// The engine keeps summaries current by itself (mostly overnight); while it writes a new
// version (3-5 s) the card checks again every few seconds, for about half a minute at most.
const POLL_MS = 4000;
const MAX_POLLS = 8;

const SECTIONS = [
  ['profile', 'Profil'],
  ['goalsAndHabits', 'Ciljevi i navike'],
  ['activity', 'Aktivnost'],
  ['purchases', 'Kupovine'],
  ['conversations', 'Razgovori sa asistentom'],
];

const TRIGGERS = {
  auto: 'automatski',
  scheduled: 'zakazano',
  manual: 'ručno',
  debug: 'test konzola',
};

const dateTime = (d) => formatDate(d, { hour: '2-digit', minute: '2-digit' });

const StatusTag = ({ status }) => {
  if (status === 'refreshing' || status === 'preparing') {
    return (
      <Tag icon={<SyncOutlined spin />} color="processing">
        {status === 'preparing' ? 'Priprema se' : 'Ažurira se'}
      </Tag>
    );
  }
  if (status === 'stalled') {
    return <Tag color="default">Ažuriranje traje duže, osvežite kasnije</Tag>;
  }
  // 'outdated' (changed less than 15 min after the last version) shows nothing: the next
  // view or the nightly job refreshes it.
  return null;
};

StatusTag.propTypes = { status: PropTypes.string };

const isWriting = (status) => status === 'refreshing' || status === 'preparing';

const AttentionPoints = ({ items }) =>
  items?.length ? (
    <Alert
      type="warning"
      showIcon
      className={styles.aiSummary__attention}
      message="Na šta obratiti pažnju"
      description={
        <ul className={styles.aiSummary__list}>
          {items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      }
    />
  ) : null;

AttentionPoints.propTypes = { items: PropTypes.arrayOf(PropTypes.string) };

// "Tema: činjenica" lines; the topic is bolded so the list scans quickly.
const KeyPoints = ({ items }) =>
  items?.length ? (
    <ul className={styles.aiSummary__keyPoints}>
      {items.map((item) => {
        const cut = item.indexOf(':');
        return (
          <li key={item}>
            {cut > 0 && cut < 30 ? (
              <>
                <strong>{item.slice(0, cut)}:</strong>
                {item.slice(cut + 1)}
              </>
            ) : (
              item
            )}
          </li>
        );
      })}
    </ul>
  ) : null;

KeyPoints.propTypes = { items: PropTypes.arrayOf(PropTypes.string) };

// Points, mentor, free delivery, cart and unfinished payments as they are right now,
// computed by the engine without AI and always shown. The summary text above mentions
// them only when they matter, and describes the moment it was written.
const CurrentState = ({ current }) =>
  current?.commerce?.length ? (
    <>
      <h4>Trenutno stanje</h4>
      <Descriptions
        size="small"
        column={1}
        bordered
        // The lines are sentences; without this they ran past the drawer's edge.
        styles={{
          label: { width: 150, verticalAlign: 'top' },
          content: { whiteSpace: 'normal', overflowWrap: 'anywhere' },
        }}
        items={current.commerce.map((c) => ({
          key: c.kind,
          label: c.title,
          children: c.text,
        }))}
      />
      <Typography.Text type="secondary" className={styles.aiSummary__note}>
        Izračunato {dateTime(current.computedAt)}, bez AI-ja.
      </Typography.Text>
    </>
  ) : null;

CurrentState.propTypes = {
  current: PropTypes.shape({
    computedAt: PropTypes.string,
    commerce: PropTypes.arrayOf(
      PropTypes.shape({
        kind: PropTypes.string,
        title: PropTypes.string,
        text: PropTypes.string,
      })
    ),
  }),
};

const HealthPanel = ({ health }) => {
  if (!health || health.loading) return <Spin size="small" />;
  if (health.error)
    return <Alert type="info" showIcon message={health.error} />;
  const h = health.data;
  if (h.access === 'denied') {
    return <Typography.Text type="secondary">{h.reason}</Typography.Text>;
  }
  if (!h.answers) {
    return (
      <Typography.Text type="secondary">
        Korisnik nije odgovorio na zdravstvena pitanja.
      </Typography.Text>
    );
  }
  const { consent, sections } = h.answers;
  return (
    <div className={styles.aiSummary__health}>
      <div>
        <Tag color={consent?.given ? 'success' : 'warning'}>
          {consent?.given
            ? `Saglasnost data${consent.givenAt ? ` ${formatDate(consent.givenAt)}` : ''}`
            : 'Saglasnost nije zabeležena'}
        </Tag>
        <Tag>
          {h.inLatestSummaryInput ? 'Korišćeno u sažetku' : 'Nije slato AI-ju'}
        </Tag>
      </div>
      {sections.map((section) => (
        <Descriptions
          key={section.title}
          title={section.title}
          size="small"
          column={1}
          bordered
          items={section.items.map((item, i) => ({
            key: `${section.title}-${i}`,
            label: item.label,
            children: item.value,
          }))}
        />
      ))}
    </div>
  );
};

HealthPanel.propTypes = { health: PropTypes.object };

export default function AiSummaryCard({ userId }) {
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [version, setVersion] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [health, setHealth] = useState(null);
  const [polls, setPolls] = useState(0);
  // Only the newest request may update the card: a poll for the latest version must not
  // overwrite an older version the admin just picked, or the other way round.
  const requestSeq = useRef(0);

  // The parent mounts a new card per user (key), so no state needs resetting here.
  const load = useCallback(
    async (v) => {
      const mine = ++requestSeq.current;
      setLoading(true);
      const { data, success, status, message } =
        await aiSummaryApi.fetchUserSummary(userId, v);
      if (mine !== requestSeq.current) return;
      setLoading(false);
      if (status === 401 || status === 403) {
        setHidden(true);
        return;
      }
      if (!success) {
        setError(message);
        return;
      }
      setError(null);
      setView(data);
    },
    [userId]
  );

  useEffect(() => {
    load(null);
  }, [load]);

  // A new version is being written: look again shortly, a few times at most.
  useEffect(() => {
    if (version || !view || !isWriting(view.status) || polls >= MAX_POLLS) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setPolls((n) => n + 1);
      load(null);
    }, POLL_MS);
    return () => clearTimeout(timer);
  }, [view, version, polls, load]);

  // After the last poll, stop showing a spinner that nothing will update.
  const status =
    isWriting(view?.status) && polls >= MAX_POLLS ? 'stalled' : view?.status;

  const selectVersion = (v) => {
    const next = v === view?.latestVersion ? null : v;
    setVersion(next);
    load(next);
  };

  const loadHealth = async (keys) => {
    const opened = Array.isArray(keys)
      ? keys.includes('health')
      : keys === 'health';
    // Retry after an error; otherwise fetch once per drawer.
    if (!opened || (health && !health.error)) return;
    setHealth({ loading: true });
    const { data, success, message } =
      await aiSummaryApi.fetchUserHealth(userId);
    setHealth(success ? { data } : { error: message });
  };

  if (hidden) return null;

  const summary = view?.summary;
  const title = (
    <span className={styles.aiSummary__title}>
      <RobotOutlined /> AI sažetak
    </span>
  );

  let body;
  if (loading && !view) {
    body = <Skeleton active paragraph={{ rows: 2 }} title={false} />;
  } else if (error && !view) {
    body = (
      <Alert
        type="info"
        showIcon
        message="AI sažetak nije dostupan."
        description={error}
      />
    );
  } else if (!summary) {
    body = (
      <Typography.Text type="secondary">
        {status === 'preparing'
          ? 'Sažetak se priprema i pojaviće se ovde za nekoliko sekundi.'
          : 'Sažetak još nije napravljen. Sistem ga pravi automatski; pogledajte ponovo kasnije.'}
      </Typography.Text>
    );
  } else {
    body = (
      <>
        <p className={styles.aiSummary__headline}>{summary.headline}</p>
        <KeyPoints items={summary.keyPoints} />
        <AttentionPoints items={summary.attentionPoints} />
        <Button onClick={() => setDrawerOpen(true)}>Ceo sažetak</Button>
      </>
    );
  }

  return (
    <>
      <Card size="small" className={styles.aiSummary} title={title}>
        {(summary || status) && (
          <div className={styles.aiSummary__meta}>
            {summary && (
              <Typography.Text type="secondary">
                Verzija {summary.version} · {dateTime(summary.createdAt)}
              </Typography.Text>
            )}
            {!version && <StatusTag status={status} />}
          </div>
        )}
        {body}
      </Card>

      <Drawer
        title="AI sažetak korisnika"
        width="min(720px, 100vw)"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        {summary && (
          <div className={styles.aiSummary__drawer}>
            <div className={styles.aiSummary__versions}>
              <Select
                value={summary.version}
                onChange={selectVersion}
                loading={loading}
                className={styles.aiSummary__versionSelect}
                options={(view.versions || []).map((v) => ({
                  value: v.version,
                  label: `Verzija ${v.version} · ${dateTime(v.createdAt)} · ${TRIGGERS[v.trigger] || v.trigger}`,
                }))}
              />
              {version ? (
                <Tag color="default">Starija verzija</Tag>
              ) : (
                <StatusTag status={status} />
              )}
            </div>

            {/* Key points stay on the card; the drawer has the full sections instead. */}
            <p className={styles.aiSummary__headline}>{summary.headline}</p>
            <AttentionPoints items={summary.attentionPoints} />

            <h4>Promene od prošlog sažetka</h4>
            <p>{summary.changesSinceLast || '–'}</p>

            {SECTIONS.map(([key, label]) => (
              <div key={key}>
                <h4>{label}</h4>
                {view.hidden?.includes(key) ? (
                  <Typography.Text
                    type="secondary"
                    className={styles.aiSummary__note}
                  >
                    Sakriveno - vaša rola nema pristup podacima od kojih je ovaj
                    deo napisan.
                  </Typography.Text>
                ) : (
                  <p>{summary.sections[key] || '–'}</p>
                )}
              </div>
            ))}

            <CurrentState current={view.current} />

            <h4>Podaci koji nedostaju</h4>
            {summary.dataGaps?.length ? (
              <ul className={styles.aiSummary__list}>
                {summary.dataGaps.map((gap) => (
                  <li key={gap}>{gap}</li>
                ))}
              </ul>
            ) : (
              <p>–</p>
            )}

            <Collapse
              className={styles.aiSummary__healthCollapse}
              onChange={loadHealth}
              items={[
                {
                  key: 'health',
                  label: 'Zdravstveni podaci',
                  extra: <Tag>pristup se beleži</Tag>,
                  children: <HealthPanel health={health} />,
                },
              ]}
            />

            <Typography.Paragraph
              type="secondary"
              className={styles.aiSummary__note}
            >
              Tekst piše AI na osnovu podataka koje izračunava sistem.
              Upozorenja, promene i podaci koji nedostaju računaju se bez AI-ja.
              Zdravstveni podaci se nikada ne šalju AI-ju za ovaj prikaz. Samo
              za internu upotrebu.
            </Typography.Paragraph>
          </div>
        )}
      </Drawer>
    </>
  );
}

AiSummaryCard.propTypes = {
  userId: PropTypes.string.isRequired,
};
